import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    useGetChatProfileQuery,
    useGetGroupMessagesQuery,
    useGetChatGroupQuery
} from '@store/feature/chat/chatApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FaArrowDown } from 'react-icons/fa';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import React, {
    useState,
    useRef,
    useCallback,
    useEffect,
    useMemo
} from 'react';
import MessageBox from '../GroupMessageBox';
import SendGroupChat from './SendGroupChat';
import { Link, useLocation } from 'react-router-dom';
import { GAS_PATH, ELECTRICITY_PATH, COMMON_PATH } from '@utils';
import GroupTopBar from './GroupTopBar';
import { useReadGroupMessageMutation } from '@store/feature/chat/chatApi';

const PAGE_SIZE = 50;

const GroupChat = ({ handleGoBack, handleToggleShowChat, navigationStack }) => {
    const location = useLocation();
    const id = navigationStack[navigationStack.length - 1];
    const { data: profile } = useGetChatProfileQuery();
    const { data: groupProfile } = useGetChatGroupQuery(id);

    const [readGroupMessage] = useReadGroupMessageMutation();

    // Pagination state
    const [before, setBefore] = useState(null);
    const [allMessages, setAllMessages] = useState([]);
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);

    // Scroll state
    const [showScrollButton, setShowScrollButton] = useState(false);
    const bottomRef = useRef(null);
    const messagesWrapperRef = useRef(null);
    const topMessageRef = useRef(null);

    // Control flags
    const hasInitiallyScrolled = useRef(false);
    const shouldScrollToBottom = useRef(false);
    const isNewChatSession = useRef(false);
    const wasAtBottomRef = useRef(true); // Track if user was at bottom before message update

    // API for paginated messages
    const {
        data: page,
        refetch,
        isFetching
    } = useGetGroupMessagesQuery(
        {
            groupId: id,
            before: before,
            limit: PAGE_SIZE
        },
        {
            refetchOnMountOrArgChange: true,
            pollingInterval: 3000,
            skip: !id
        }
    );

    // Reset chat state when id changes
    const prevIdRef = useRef(id);
    useEffect(() => {
        if (prevIdRef.current && prevIdRef.current !== id) {
            setAllMessages([]);
            setBefore(null);
            setIsLoadingOlderMessages(false);
            setHasMoreMessages(true);
            setShowScrollButton(false);
            hasInitiallyScrolled.current = false;
            shouldScrollToBottom.current = false;
            isNewChatSession.current = true;
            refetch();
        }
        prevIdRef.current = id;
    }, [id, refetch]);

    // Track previous after value and reset before when new messages arrive
    const prevAfterRef = useRef(page?.after);
    useEffect(() => {
        if (
            page?.after &&
            prevAfterRef.current &&
            page.after !== prevAfterRef.current
        ) {
            setBefore(null);
        }
        prevAfterRef.current = page?.after;
    }, [page?.after]);

    // Merge new messages
    useEffect(() => {
        // Store if user was at bottom before updating messages
        wasAtBottomRef.current = isAtBottom();

        if (!page?.items || !Array.isArray(page.items)) {
            setIsLoadingOlderMessages(false);
            return;
        }
        if (page.items.length === 0) {
            setIsLoadingOlderMessages(false);
            setHasMoreMessages(false);
            return;
        }
        const ascending = [...page.items].reverse();
        const unseen = ascending.filter(
            (m) => !allMessages.some((msg) => msg.uuid === m.uuid)
        );
        const hasMore = page.items.length === PAGE_SIZE;
        setHasMoreMessages(hasMore);

        if (before === null) {
            setAllMessages((prev) => {
                const existingIds = new Set(prev?.map((msg) => msg.uuid));
                const newUniqueMessages = unseen.filter(
                    (msg) => !existingIds.has(msg.uuid)
                );
                if (newUniqueMessages?.length === 0) return prev;
                const updatedMessages = [...prev, ...newUniqueMessages];
                if (
                    isNewChatSession.current &&
                    prev.length === 0 &&
                    newUniqueMessages?.length > 0
                ) {
                    setTimeout(() => {
                        if (bottomRef?.current) {
                            bottomRef?.current.scrollIntoView({
                                behavior: 'auto'
                            });
                        }
                    }, 100);
                    setTimeout(() => {
                        if (bottomRef?.current) {
                            bottomRef?.current.scrollIntoView({
                                behavior: 'smooth'
                            });
                        }
                    }, 300);
                    isNewChatSession.current = false;
                }
                return updatedMessages;
            });
            setIsLoadingOlderMessages(false);
        } else {
            const wrapper = messagesWrapperRef.current;
            if (wrapper) {
                const prevHeight = wrapper.scrollHeight;
                setAllMessages((prev) => {
                    const existingIds = new Set(prev?.map((msg) => msg.uuid));
                    const newUniqueMessages = unseen.filter(
                        (msg) => !existingIds.has(msg.uuid)
                    );
                    if (newUniqueMessages?.length === 0) return prev;
                    return [...newUniqueMessages, ...prev];
                });
                requestAnimationFrame(() => {
                    if (wrapper) {
                        wrapper.scrollTop = wrapper.scrollHeight - prevHeight;
                    }
                });
            }
            setIsLoadingOlderMessages(false);
        }
    }, [page?.items, before]);

    // Auto-scroll logic
    useEffect(() => {
        if (!allMessages.length) return;
        // Only scroll if user was at bottom before new messages arrived
        if (wasAtBottomRef.current) {
            setTimeout(() => scrollToBottom('smooth'), 80);
            wasAtBottomRef.current = false; // Reset after scroll
            return;
        }
        const lastMessage = allMessages[allMessages.length - 1];
        const isOwnMessage = lastMessage?.from === profile?.uuid;
        if (isOwnMessage) {
            scrollToBottom('smooth');
            return;
        }
        if (!hasInitiallyScrolled.current) {
            setTimeout(() => {
                scrollToBottom('auto');
                setTimeout(() => scrollToBottom('smooth'), 100);
            }, 50);
            hasInitiallyScrolled.current = true;
            return;
        }
        if (shouldScrollToBottom.current) {
            setTimeout(() => scrollToBottom('smooth'), 100);
            shouldScrollToBottom.current = false;
            return;
        }
        if (isAtBottom()) {
            scrollToBottom('smooth');
        }
    }, [allMessages, profile?.uuid]);

    // Scroll helpers
    const scrollToBottom = useCallback((behavior = 'smooth') => {
        if (bottomRef?.current) {
            bottomRef?.current.scrollIntoView({ behavior });
        }
    }, []);
    const isAtBottom = useCallback(() => {
        if (!messagesWrapperRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } =
            messagesWrapperRef.current;
        return scrollHeight - scrollTop - clientHeight <= 50;
    }, []);

    // Show/hide scroll button
    const handleScroll = useCallback(() => {
        const atBottom = isAtBottom();
        setShowScrollButton(!atBottom && allMessages.length > 0);
    }, [isAtBottom, allMessages.length]);
    useEffect(() => {
        const wrapper = messagesWrapperRef.current;
        if (!wrapper) return;
        wrapper.addEventListener('scroll', handleScroll);
        return () => wrapper.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Infinite scroll for older messages
    useEffect(() => {
        if (
            !allMessages.length ||
            !topMessageRef.current ||
            isLoadingOlderMessages ||
            !hasMoreMessages ||
            isFetching
        ) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    hasMoreMessages &&
                    !isLoadingOlderMessages
                ) {
                    setIsLoadingOlderMessages(true);
                    const oldestMessage = allMessages[0];
                    if (oldestMessage) {
                        setBefore(oldestMessage.created_at);
                    }
                }
            },
            {
                threshold: 0.1,
                rootMargin: '20px'
            }
        );
        const currentRef = topMessageRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => {
            if (currentRef) observer.unobserve(currentRef);
        };
    }, [allMessages, hasMoreMessages, isLoadingOlderMessages, isFetching]);

    useEffect(() => {
        if (id && allMessages.length > 0) {
            const lastMessage = allMessages[allMessages.length - 1];
            if (lastMessage && lastMessage.from !== profile?.uuid) {
                readGroupMessage({ groupId: id, messageId: lastMessage.uuid });
            }
        }
    }, [id, profile, allMessages, readGroupMessage]);

    // Handle sent messages
    const handleMessageSent = useCallback(() => {
        setShowScrollButton(false);
        shouldScrollToBottom.current = true;
        setBefore(null);
        refetch();
        setTimeout(() => {
            if (bottomRef?.current) {
                bottomRef?.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, 200);
    }, [refetch]);

    // Group messages by sender
    const groupMessagesById = useCallback((messages) => {
        if (!messages) return [];
        const groupedMessages = [];
        let currentGroup = [];

        messages?.forEach((message, index) => {
            if (
                currentGroup.length === 0 ||
                currentGroup[0].from === message.from
            ) {
                currentGroup.push(message);
            } else {
                groupedMessages.push(currentGroup);
                currentGroup = [message];
            }
        });
        if (currentGroup.length > 0) {
            groupedMessages.push(currentGroup);
        }
        return groupedMessages;
    }, []);

    // Enhanced date formatting
    const formatDate = useCallback((timestampOrIso) => {
        const date =
            typeof timestampOrIso === 'number'
                ? new Date(timestampOrIso * 1000)
                : new Date(timestampOrIso);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const messageDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        );
        const todayDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        );
        const yesterdayDate = new Date(
            yesterday.getFullYear(),
            yesterday.getMonth(),
            yesterday.getDate()
        );
        if (messageDate.getTime() === todayDate.getTime()) {
            return 'Today';
        } else if (messageDate.getTime() === yesterdayDate.getTime()) {
            return 'Yesterday';
        } else {
            const diffTime = todayDate.getTime() - messageDate.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays <= 7 && diffDays > 0) {
                return date.toLocaleDateString('en-US', { weekday: 'long' });
            } else {
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year:
                        messageDate.getFullYear() !== todayDate.getFullYear()
                            ? 'numeric'
                            : undefined
                });
            }
        }
    }, []);

    // Memoized message list with date headers
    const messageList = useMemo(() => {
        const groupedMessages = groupMessagesById(allMessages);
        return groupedMessages.map((group, groupIndex) => {
            const firstMessage = group[0];
            const previousGroup =
                groupIndex > 0 ? groupedMessages[groupIndex - 1] : null;
            const previousMessage = previousGroup
                ? previousGroup[previousGroup.length - 1]
                : null;
            const currentDate = new Date(
                firstMessage.created_at
            ).toDateString();
            const previousDate = previousMessage
                ? new Date(previousMessage.created_at).toDateString()
                : null;
            const showDateHeader =
                groupIndex === 0 || currentDate !== previousDate;
            return (
                <React.Fragment key={`group-${groupIndex}`}>
                    {showDateHeader && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                my: 3,
                                width: '100%',
                                position: 'relative'
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                    backdropFilter: 'blur(8px)',
                                    borderRadius: '20px',
                                    px: 3,
                                    py: 1,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    minWidth: '80px',
                                    textAlign: 'center'
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#374151',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        textTransform: 'capitalize',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {formatDate(firstMessage.created_at)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <MessageBox groupMessages={group} profile={profile} />
                </React.Fragment>
            );
        });
    }, [allMessages, formatDate, profile, groupMessagesById]);

    // ...existing code for header...
    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box
                sx={{
                    height: '50px',
                    backgroundColor: '#27485D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 10px'
                }}
            >
                {/* ...existing code for header... */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center'
                    }}
                    onClick={handleGoBack}
                >
                    <IconButton sx={{ padding: '0', margin: '0' }}>
                        <ArrowBackIcon
                            sx={{ color: '#fff', fontSize: '1.5rem' }}
                        />
                    </IconButton>
                    <Typography
                        variant="body2"
                        sx={{ color: 'white', fontWeight: 'medium' }}
                    >
                        Go to Chats
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Box
                        component={Link}
                        to={`${
                            location.pathname.includes('gas')
                                ? `${GAS_PATH.CHAT}/group/${id}?tab=groups`
                                : location.pathname.includes('electricity')
                                ? `${ELECTRICITY_PATH.CHAT}/group/${id}?tab=groups`
                                : `${COMMON_PATH.CHAT}/group/${id}?tab=groups`
                        }`}
                    >
                        <IconButton>
                            <OpenInFullIcon
                                sx={{ color: '#fff', fontSize: '1.2rem' }}
                            />
                        </IconButton>
                    </Box>
                    <IconButton onClick={handleToggleShowChat}>
                        <CloseIcon sx={{ color: '#fff', fontSize: '1.5rem' }} />
                    </IconButton>
                </Box>
            </Box>
            <GroupTopBar groupProfile={groupProfile} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    height: 'calc(100% - 100px)'
                }}
            >
                <Box
                    ref={messagesWrapperRef}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        px: '10px',
                        pt: '10px',
                        overflowY: 'auto',
                        height: 'calc(100% - 60px)',
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                            borderRadius: '4px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '4px',
                            '&:hover': { backgroundColor: '#a1a1a1' }
                        },
                        scrollbarColor: '#c1c1c1 #f1f1f1'
                    }}
                >
                    {isLoadingOlderMessages && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 1,
                                gap: 1
                            }}
                        >
                            <span className="animate-spin text-[#2FACFE] text-lg">
                                ⏳
                            </span>
                            <Typography
                                variant="caption"
                                sx={{ color: 'teal', fontSize: '0.85rem' }}
                            >
                                Loading older messages...
                            </Typography>
                        </Box>
                    )}
                    <div ref={topMessageRef} />
                    {allMessages.length > 0 ? (
                        <>{messageList}</>
                    ) : (
                        <Box className="flex justify-center py-20">
                            <Typography>No messages available!</Typography>
                        </Box>
                    )}
                    <div ref={bottomRef} className="w-full h-[1px] mb-2" />
                </Box>
                {showScrollButton && (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                bottom: '8px',
                                transform: 'translateX(-50%)',
                                zIndex: 1000
                            }}
                        >
                            <button
                                className="rounded-full flex justify-center items-center bg-[#2FACFE] w-8 h-8 shadow-lg hover:bg-[#1e8fd4] transition-colors"
                                onClick={() => {
                                    shouldScrollToBottom.current = true;
                                    scrollToBottom('smooth');
                                    setShowScrollButton(false);
                                }}
                            >
                                <FaArrowDown color="white" />
                            </button>
                        </Box>
                    </Box>
                )}
                <SendGroupChat
                    id={groupProfile?.uuid}
                    groupProfile={groupProfile}
                    userProfile={profile}
                    onMessageSent={handleMessageSent}
                />
            </Box>
        </Box>
    );
};

export default GroupChat;
